import { maxBy, sortBy, reverse } from 'lodash';
import { batchActions, countryFacetedCountsToCountryDemographics } from './utils';
import { setData } from './data';
import { setStatus, unsetStatus } from './status';
import { LOADING, ERROR, selectPodcastDemographicsStatus, selectDateRange } from './selectors';
import {
  calculateRoundedPercentage,
  distributeObjectFractals,
  calculateRawPercentage,
} from '../lib/Formatter';
import { toJSON, PODCASTERS_SERVICE } from '../lib/Request';
import {
  DateRangeWithLabel,
  Thunk,
  AgeFacetedCounts,
  CountryFactedCounts,
  GenderedCounts,
  GenderDemographics,
  GenderDemographicsPercentagePerRange,
  GenderDemographicsPercentagePerGender,
  CountryDemographics,
} from '../types';

export const loadPodcastDemographics = (podcastId: string): Thunk => async (
  dispatch,
  getState,
  { request },
) => {
  const key = 'podcastDemographics';
  const { startDate, endDate } = selectDateRange(getState()) as DateRangeWithLabel;
  const endpoint = `${PODCASTERS_SERVICE}/v0/shows/${podcastId}/aggregate?start=${startDate}&end=${endDate}`;

  /* istanbul ignore next */
  if (selectPodcastDemographicsStatus(getState()) === LOADING) return;

  dispatch(setStatus({ [key]: LOADING }, 'audience/LOAD_PODCAST_DEMOGRAPHICS_REQUEST'));

  try {
    const data: {
      count: number;
      start: string;
      end: string;
      ageFacetedCounts: AgeFacetedCounts;
      countryFacetedCounts?: CountryFactedCounts;
      genderedCounts: GenderedCounts;
    } = await request.get(endpoint).then(toJSON);
    const {
      ageFacetedCounts: dataByAgeRange,
      count: listenerTotal,
      genderedCounts: { counts: countByGender },
    } = data;

    const genderDemographicsByAgeGroup: GenderDemographics[] = Object.keys(dataByAgeRange)
      .sort()
      .map(ageRange => {
        const { MALE = 0, FEMALE = 0, NOT_SPECIFIED = 0, NON_BINARY = 0 } = dataByAgeRange[
          ageRange
        ].counts;
        const totalAgeRangeCount = MALE + FEMALE + NOT_SPECIFIED + NON_BINARY;
        const ageRangePercentage = calculateRawPercentage(totalAgeRangeCount, listenerTotal);

        const percentagePerRange = distributeObjectFractals(
          {
            malePercentage: calculateRawPercentage(MALE, totalAgeRangeCount),
            femalePercentage: calculateRawPercentage(FEMALE, totalAgeRangeCount),
            notSpecifiedPercentage: calculateRawPercentage(NOT_SPECIFIED, totalAgeRangeCount),
            nonBinaryPercentage: calculateRawPercentage(NON_BINARY, totalAgeRangeCount),
          },
          100,
        ) as GenderDemographicsPercentagePerRange;

        const percentagePerGender: GenderDemographicsPercentagePerGender = {
          male: calculateRawPercentage(MALE, listenerTotal),
          female: calculateRawPercentage(FEMALE, listenerTotal),
          notSpecified: calculateRawPercentage(NOT_SPECIFIED, listenerTotal),
          nonBinary: calculateRawPercentage(NON_BINARY, listenerTotal),
        };

        return {
          ageRange,
          ageRangePercentage,
          ...percentagePerGender,
          ...percentagePerRange,
        };
      });

    const percentageByGender = [
      {
        gender: 'female',
        percentage: calculateRoundedPercentage(countByGender.FEMALE, listenerTotal),
      },
      {
        gender: 'male',
        percentage: calculateRoundedPercentage(countByGender.MALE, listenerTotal),
      },
      {
        gender: 'notSpecified',
        percentage: calculateRoundedPercentage(countByGender.NOT_SPECIFIED, listenerTotal),
      },
      {
        gender: 'nonBinary',
        percentage: calculateRoundedPercentage(countByGender.NON_BINARY, listenerTotal),
      },
    ];

    /* istanbul ignore next */
    const countryCounts = data.countryFacetedCounts || {};
    const countryDemographics = countryFacetedCountsToCountryDemographics(countryCounts);

    const genderMax = maxBy(percentageByGender, g => g.percentage);
    const countryMax = countryDemographics[0];

    const demographicsMetadata = {
      ...genderMax,
      ...countryMax,
    };

    const normalized = {
      genderDemographicsByAgeGroup,
      percentageByGender,
      countryDemographics,
      demographicsMetadata,
      ...data,
      countryFacetedCounts: countryCounts,
    };

    dispatch(
      batchActions(
        [setData({ [key]: normalized }), unsetStatus([key])],
        'audience/LOAD_PODCAST_DEMOGRAPHICS_SUCCESS',
      ),
    );
  } catch (err) {
    switch (err.name) {
      case 'ResponseError':
      case 'TokenError': {
        dispatch(setStatus({ [key]: ERROR }, 'audience/LOAD_PODCAST_DEMOGRAPHICS_ERROR'));
        break;
      }

      /* istanbul ignore next */
      default:
        throw err;
    }
  }
};
